import React, { useState, useEffect, useContext, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import { 
  Clock, 
  ChevronLeft, 
  ChevronRight, 
  AlertTriangle,
  CheckCircle,
  XCircle,
  FileQuestion,
  ArrowLeft,
  Trophy
} from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MockTestPage = () => {
  const { courseId } = useParams();
  const navigate = useNavigate();
  const { isAuthenticated } = useContext(AuthContext);
  
  const [course, setCourse] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [answers, setAnswers] = useState({});
  const [timeLeft, setTimeLeft] = useState(45 * 60); // 45 minutes
  const [loading, setLoading] = useState(true);
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    if (!isAuthenticated) {
      toast.error('Please login to take mock test');
      navigate('/auth/login');
      return;
    }
    fetchMockTestData();
  }, [courseId, isAuthenticated]);

  // Timer
  useEffect(() => {
    if (submitted || loading) return;
    
    const timer = setInterval(() => {
      setTimeLeft((prev) => {
        if (prev <= 1) {
          handleSubmit();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [submitted, loading]);

  const fetchMockTestData = async () => {
    try {
      // Get course details
      const courseRes = await axios.get(`${API}/assessment/courses/${courseId}`);
      setCourse(courseRes.data);

      // Get question paper
      const papersRes = await axios.get(`${API}/assessment/papers/course/${courseId}`);
      if (papersRes.data.length === 0) {
        toast.error('No question paper found for this course');
        navigate('/assessment');
        return;
      }

      const paperId = papersRes.data[0].id;

      // Get mock test questions (40 random)
      const questionsRes = await axios.get(`${API}/assessment/mock-test/${paperId}`);
      setQuestions(questionsRes.data.questions || []);
      
    } catch (error) {
      toast.error('Failed to load mock test');
      navigate('/assessment');
    } finally {
      setLoading(false);
    }
  };

  const handleAnswerSelect = (questionId, answer) => {
    setAnswers(prev => ({ ...prev, [questionId]: answer }));
  };

  const handleSubmit = useCallback(() => {
    if (submitted) return;
    
    // Calculate result locally
    let correct = 0;
    let wrong = 0;
    
    questions.forEach(q => {
      const userAnswer = answers[q.id];
      if (userAnswer) {
        if (userAnswer.toUpperCase() === q.correct_answer.toUpperCase()) {
          correct++;
        } else {
          wrong++;
        }
      }
    });

    const total = questions.length;
    const percentage = (correct / total) * 100;
    const passed = percentage >= 50;

    setResult({
      correct,
      wrong,
      unanswered: total - correct - wrong,
      total,
      percentage,
      passed,
      answers: answers,
      questions: questions
    });
    
    setSubmitted(true);
    toast.success('Mock test submitted!');
  }, [answers, questions, submitted]);

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin h-12 w-12 border-4 border-purple-600 border-t-transparent rounded-full mx-auto mb-4"></div>
          <p className="text-slate-600">Loading mock test...</p>
        </div>
      </div>
    );
  }

  // Results Screen
  if (submitted && result) {
    return (
      <div className="min-h-screen bg-slate-100 py-8 px-4">
        <div className="max-w-3xl mx-auto">
          {/* Result Card */}
          <div className="bg-white rounded-xl shadow-lg p-8 mb-6">
            <div className="text-center mb-6">
              <div className={`w-20 h-20 rounded-full mx-auto mb-4 flex items-center justify-center ${
                result.passed ? 'bg-emerald-100' : 'bg-red-100'
              }`}>
                {result.passed ? (
                  <Trophy className="h-10 w-10 text-emerald-600" />
                ) : (
                  <XCircle className="h-10 w-10 text-red-600" />
                )}
              </div>
              <h1 className="text-2xl font-bold text-slate-900 mb-2">
                Mock Test {result.passed ? 'Passed!' : 'Not Passed'}
              </h1>
              <p className="text-slate-600">
                {course?.name} - Practice Mode
              </p>
            </div>

            {/* Score */}
            <div className="grid grid-cols-3 gap-4 mb-6">
              <div className="bg-emerald-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-emerald-600">{result.correct}</p>
                <p className="text-sm text-emerald-700">Correct</p>
              </div>
              <div className="bg-red-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-red-600">{result.wrong}</p>
                <p className="text-sm text-red-700">Wrong</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-4 text-center">
                <p className="text-2xl font-bold text-slate-600">{result.unanswered}</p>
                <p className="text-sm text-slate-700">Unanswered</p>
              </div>
            </div>

            <div className="bg-purple-50 rounded-lg p-4 text-center mb-6">
              <p className="text-4xl font-bold text-purple-600">{result.percentage.toFixed(1)}%</p>
              <p className="text-purple-700">Your Score</p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-6">
              <p className="text-amber-800 text-sm text-center">
                <FileQuestion className="h-4 w-4 inline mr-1" />
                This was a practice test. No certificate is issued for mock tests.
              </p>
            </div>

            <div className="flex gap-3">
              <Button 
                variant="outline" 
                className="flex-1"
                onClick={() => navigate('/assessment')}
              >
                <ArrowLeft className="h-4 w-4 mr-2" />
                Back to Portal
              </Button>
              <Button 
                className="flex-1 bg-purple-600 hover:bg-purple-700"
                onClick={() => {
                  setSubmitted(false);
                  setResult(null);
                  setAnswers({});
                  setCurrentIndex(0);
                  setTimeLeft(45 * 60);
                  fetchMockTestData();
                }}
              >
                Try Again
              </Button>
            </div>
          </div>

          {/* Answer Review */}
          <div className="bg-white rounded-xl shadow-lg p-6">
            <h2 className="text-lg font-bold text-slate-900 mb-4">Answer Review</h2>
            <div className="space-y-4">
              {result.questions.map((q, idx) => {
                const userAnswer = result.answers[q.id];
                const isCorrect = userAnswer?.toUpperCase() === q.correct_answer.toUpperCase();
                
                return (
                  <div key={q.id} className={`p-4 rounded-lg border ${
                    !userAnswer ? 'bg-slate-50 border-slate-200' :
                    isCorrect ? 'bg-emerald-50 border-emerald-200' : 'bg-red-50 border-red-200'
                  }`}>
                    <div className="flex items-start gap-3">
                      <span className="font-medium text-slate-500">Q{idx + 1}.</span>
                      <div className="flex-1">
                        <p className="text-slate-900 mb-2">{q.question_text}</p>
                        <div className="flex items-center gap-4 text-sm">
                          <span className="text-slate-600">
                            Your answer: <strong>{userAnswer || 'Not answered'}</strong>
                          </span>
                          <span className="text-emerald-600">
                            Correct: <strong>{q.correct_answer}</strong>
                          </span>
                          {userAnswer && (
                            isCorrect ? (
                              <CheckCircle className="h-4 w-4 text-emerald-600" />
                            ) : (
                              <XCircle className="h-4 w-4 text-red-600" />
                            )
                          )}
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentIndex];

  return (
    <div className="min-h-screen bg-slate-100">
      {/* Header */}
      <div className="bg-purple-600 text-white py-4 px-6 sticky top-0 z-10">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-3">
            <FileQuestion className="h-6 w-6" />
            <div>
              <h1 className="font-bold">Mock Test: {course?.name}</h1>
              <p className="text-purple-200 text-sm">Practice Mode</p>
            </div>
          </div>
          <div className={`flex items-center gap-2 px-4 py-2 rounded-full ${
            timeLeft < 300 ? 'bg-red-500' : 'bg-purple-500'
          }`}>
            <Clock className="h-5 w-5" />
            <span className="font-mono font-bold">{formatTime(timeLeft)}</span>
          </div>
        </div>
      </div>

      <div className="max-w-4xl mx-auto py-6 px-4">
        {/* Question Navigation */}
        <div className="bg-white rounded-lg p-4 mb-6">
          <p className="text-sm text-slate-600 mb-3">Question Navigator</p>
          <div className="flex flex-wrap gap-2">
            {questions.map((q, idx) => (
              <button
                key={q.id}
                onClick={() => setCurrentIndex(idx)}
                className={`w-10 h-10 rounded-lg font-medium text-sm ${
                  currentIndex === idx
                    ? 'bg-purple-600 text-white'
                    : answers[q.id]
                    ? 'bg-emerald-100 text-emerald-700 border border-emerald-300'
                    : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                }`}
              >
                {idx + 1}
              </button>
            ))}
          </div>
          <div className="flex gap-4 mt-3 text-xs text-slate-500">
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-purple-600 rounded"></span> Current
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-emerald-100 border border-emerald-300 rounded"></span> Answered
            </span>
            <span className="flex items-center gap-1">
              <span className="w-3 h-3 bg-slate-100 rounded"></span> Not Answered
            </span>
          </div>
        </div>

        {/* Question Card */}
        {currentQuestion && (
          <div className="bg-white rounded-xl shadow-sm p-6 mb-6">
            <div className="flex items-center justify-between mb-4">
              <span className="text-sm font-medium text-purple-600">
                Question {currentIndex + 1} of {questions.length}
              </span>
              <span className="text-sm text-slate-500">
                2.5 marks
              </span>
            </div>

            <h2 className="text-lg text-slate-900 mb-6">{currentQuestion.question_text}</h2>

            <div className="space-y-3">
              {['A', 'B', 'C', 'D'].map((option) => (
                <label
                  key={option}
                  className={`flex items-center p-4 rounded-lg border-2 cursor-pointer transition-all ${
                    answers[currentQuestion.id] === option
                      ? 'border-purple-600 bg-purple-50'
                      : 'border-slate-200 hover:border-purple-300'
                  }`}
                >
                  <input
                    type="radio"
                    name={`question-${currentQuestion.id}`}
                    value={option}
                    checked={answers[currentQuestion.id] === option}
                    onChange={() => handleAnswerSelect(currentQuestion.id, option)}
                    className="sr-only"
                  />
                  <span className={`w-8 h-8 rounded-full flex items-center justify-center mr-4 ${
                    answers[currentQuestion.id] === option
                      ? 'bg-purple-600 text-white'
                      : 'bg-slate-100 text-slate-600'
                  }`}>
                    {option}
                  </span>
                  <span className="text-slate-700">
                    {currentQuestion[`option_${option.toLowerCase()}`]}
                  </span>
                </label>
              ))}
            </div>
          </div>
        )}

        {/* Navigation */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={() => setCurrentIndex(Math.max(0, currentIndex - 1))}
            disabled={currentIndex === 0}
          >
            <ChevronLeft className="h-4 w-4 mr-2" />
            Previous
          </Button>

          <div className="flex gap-3">
            {currentIndex < questions.length - 1 ? (
              <Button
                onClick={() => setCurrentIndex(currentIndex + 1)}
                className="bg-purple-600 hover:bg-purple-700"
              >
                Next
                <ChevronRight className="h-4 w-4 ml-2" />
              </Button>
            ) : (
              <Button
                onClick={handleSubmit}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Submit Test
              </Button>
            )}
          </div>
        </div>

        {/* Warning */}
        <div className="mt-6 bg-amber-50 border border-amber-200 rounded-lg p-4 flex items-start gap-3">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-800">
            <p className="font-medium">Mock Test - Practice Mode</p>
            <p>This is a practice test. Results are for self-assessment only. No certificate will be issued.</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MockTestPage;
